import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-683fa68a"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { id: "pointCloudContainer" }, null, -1))

import {
  onMounted,
  ref,
  nextTick,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import {
  Scene,
  PerspectiveCamera,
  Vector3,
  WebGLRenderer,
  AxesHelper,
  BoxGeometry,
  MeshBasicMaterial,
  Mesh,
  Camera,
  Euler,
  MathUtils,
  Raycaster,
  SphereGeometry,
  TextureLoader,
  BufferGeometry,
  Line,
} from 'three';
import { OrbitControls } from '@three-ts/orbit-controls';
import { IVisibilityUpdateResult, PointCloudOctree, Potree } from '@pnext/three-loader';
import { ImagePoint } from '@/models/image';
import { XYZ } from '@/models/geometry';
import { PointCloudData } from '@/models/point-cloud';
import { EnumActionTypes } from '@/store/modules/loading';


export default /*#__PURE__*/_defineComponent({
  __name: 'Merged',
  props: {
    timestampLastResized: null
  },
  setup(__props: any) {

const props = __props as {
  timestampLastResized: number,
}

const store = useStore();



let containerElem = {} as HTMLElement;
let scene = {} as Scene;
let camera = {} as PerspectiveCamera;
let renderer = {} as WebGLRenderer;
let orbitControls = {} as OrbitControls;
let image360: Mesh<SphereGeometry, MeshBasicMaterial> | null = null;
let potree = {} as Potree;
const pointClouds = [] as PointCloudOctree[];
let refRequestAnimationFrame = null as number | null;
const isPointCloudLoading = ref(false);

const rendererUpdate = () => {
  isPointCloudLoading.value = true;
  console.log('upd');
  refRequestAnimationFrame = requestAnimationFrame(rendererUpdate);
  if (!potree.updatePointClouds) {
    console.log('not ready');
    return;
  }
  const updatePointCloudsResult = potree.updatePointClouds(pointClouds as PointCloudOctree[], camera, renderer);
  if (!updatePointCloudsResult.nodeLoadPromises.length) {
    cancelAnimationFrame(refRequestAnimationFrame);
    isPointCloudLoading.value = false;
  }
  renderer.render(scene, camera);
};

const initScene = () => {
  containerElem = document.getElementById('pointCloudContainer') || {} as HTMLElement;
  scene = new Scene();
  if (!containerElem) return;
  // eslint-disable-next-line
  camera = new PerspectiveCamera(75, containerElem.clientWidth / containerElem.clientHeight, 0.1, 1000);
  camera.up.set(0, 0, 1);
  camera.position.set(0, 0, 0);
  renderer = new WebGLRenderer({ antialias: true });
  renderer.setClearColor('#000000');
  renderer.setSize(containerElem.clientWidth, containerElem.clientHeight);
  containerElem.appendChild(renderer.domElement);
  orbitControls = new OrbitControls(camera, renderer.domElement);
  orbitControls.addEventListener('change', rendererUpdate);
  orbitControls.target.set(-0, 0, 0);
  orbitControls.update();
  potree = new Potree();
  potree.pointBudget = 1_000_000;
  const pointCloudData: PointCloudData[] = store.getters['geometry/getPointCloudData'];
  const pointCloudPromises = pointCloudData
    .map((x) => potree.loadPointCloud('cloud.js', (relativeUrl) => (`${x.directory}/${relativeUrl}`)));
  Promise.all(pointCloudPromises).then((p) => {
    p.forEach((pointCloud) => {
      // eslint-disable-next-line
      pointCloud.material.size = 1.0;
      pointClouds.push(pointCloud);
      scene.add(pointCloud);
    });
    rendererUpdate();
  });
};

const addImage = (imagePoint: ImagePoint) => {
  orbitControls.enabled = false;

  const {
    image,
    x,
    y,
    alt,
    yaw,
    pitch,
    roll,
  } = imagePoint;

  const baseUrl = 'data/equirectangular/';
  const url = `${baseUrl}${image}`;

  camera.position.set(x, y, alt);
  orbitControls.target.set(x, y + 0.0000001, alt);

  const geometry360 = new SphereGeometry(500, 60, 40);
  geometry360.scale(-1, 1, 1);

  const textureLoader = new TextureLoader();
  const texture = textureLoader.load(
    url,
    async () => {
      rendererUpdate();
      await store.dispatch(`loading/${EnumActionTypes.SET_IMAGE_LOADING}`, false);
    },
    (xhr) => console.log(`${((xhr.loaded / xhr.total) * 100)} % loaded`),
    () => console.log('an error happened'),
  );

  const material360 = new MeshBasicMaterial({ map: texture });

  if (image360) scene.remove(image360);

  image360 = new Mesh(geometry360, material360);
  image360.position.set(x, y, alt);

  // something different happens here: https://github.com/potree/potree/blob/develop/src/modules/Images360/Images360.js#L135
  // must be checked!! todo
  const euler = new Euler(
    MathUtils.degToRad(-roll + 90),
    MathUtils.degToRad(pitch),
    MathUtils.degToRad(yaw - 90),
    'ZYX',
  );
  image360.setRotationFromEuler(euler);

  scene.add(image360);

  orbitControls.enabled = true;

  orbitControls.update();
  rendererUpdate();
};

const setCameraPosition = (imagePoint: ImagePoint) => {
  if (refRequestAnimationFrame) cancelAnimationFrame(refRequestAnimationFrame);
  orbitControls.enabled = false;
  camera.position.set(imagePoint.x, imagePoint.y, imagePoint.alt);
  orbitControls.target.set(imagePoint.x, imagePoint.y + 0.0000001, imagePoint.alt);
  orbitControls.enabled = true;
  rendererUpdate();
};

const setOrientation = (orientation: Euler) => {
  if (refRequestAnimationFrame) cancelAnimationFrame(refRequestAnimationFrame);
  orbitControls.enabled = false;
  camera.setRotationFromEuler(orientation);
  renderer.render(scene, camera);
  rendererUpdate();
  orbitControls.enabled = true;
};

watch(() => store.getters['geometry/getImagePoint'], (imagePoint: ImagePoint | null) => {
  if (!imagePoint) return;
  addImage(imagePoint);
  setCameraPosition(imagePoint);
}, { deep: true });

watch(() => store.getters['geometry/getOrientation'], (orientation: Euler) => {
  setOrientation(orientation);
}, { deep: true });

onMounted(async () => {
  await nextTick(); // due to splitpanes issue when using different size than 50%
  initScene();
});


return (_ctx: any,_cache: any) => {
  const _component_v_progress_linear = _resolveComponent("v-progress-linear")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isPointCloudLoading.value)
      ? (_openBlock(), _createBlock(_component_v_progress_linear, {
          key: 0,
          color: "orange",
          indeterminate: true,
          class: "loader-bar"
        }))
      : _createCommentVNode("", true),
    _hoisted_1
  ], 64))
}
}

})